import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import {
	adminRequestKeys,
	getAdminRequestCounts,
	listAdminRequests,
} from "@/features/applications/api/admin-requests";
import type {
	ListAdminRequestsParams,
	RequestType,
} from "@/features/applications/api/types";
import {
	parseQueueOrder,
	parseQueueSort,
} from "@/features/applications/lib/queue-controls";
import type { PageLimit, RequestStatus } from "@/shared/api/types";

export function useApplicationsSearchParams() {
	const [searchParams, setSearchParams] = useSearchParams();

	const params: ListAdminRequestsParams = useMemo(() => {
		const type = (searchParams.get("type") as RequestType | null) ?? "DISCOUNT";
		const statusRaw = searchParams.getAll("status") as RequestStatus[];
		const search = searchParams.get("search") ?? undefined;
		const page = Number(searchParams.get("page") ?? "1") || 1;
		const limit = (Number(searchParams.get("limit") ?? "20") ||
			20) as PageLimit;

		return {
			type: type === "CHILD" || type === "DISCOUNT" ? type : "DISCOUNT",
			status: statusRaw.length ? statusRaw : undefined,
			search: search && search.trim().length >= 3 ? search.trim() : undefined,
			page,
			limit: ([20, 50, 100] as PageLimit[]).includes(limit) ? limit : 20,
			sort: parseQueueSort(searchParams.get("sort")),
			order: parseQueueOrder(searchParams.get("order")),
		};
	}, [searchParams]);

	const updateParams = useCallback(
		(patch: Partial<ListAdminRequestsParams> & { searchInput?: string }) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					if (patch.type) {
						next.set("type", patch.type);
						next.set("page", "1");
					}
					if ("status" in patch) {
						next.delete("status");
						for (const status of patch.status ?? []) {
							next.append("status", status);
						}
						next.set("page", "1");
					}
					if (patch.sort) {
						next.set("sort", patch.sort);
						next.set("page", "1");
					}
					if (patch.order) {
						next.set("order", patch.order);
						next.set("page", "1");
					}
					if (patch.searchInput !== undefined) {
						const value = patch.searchInput.trim();
						if (value.length >= 3) {
							next.set("search", value);
						} else {
							next.delete("search");
						}
						next.set("page", "1");
					}
					if (patch.page !== undefined) {
						next.set("page", String(patch.page));
					}
					if (patch.limit !== undefined) {
						next.set("limit", String(patch.limit));
						next.set("page", "1");
					}

					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	return { params, searchParams, updateParams };
}

export function queryRefreshInterval(ms: number) {
	return ms > 0 ? ms : false;
}

export function useApplicationsQuery(
	params: ListAdminRequestsParams,
	refreshMs = 0,
) {
	return useQuery({
		queryKey: adminRequestKeys.list(params),
		queryFn: () => listAdminRequests(params),
		placeholderData: keepPreviousData,
		refetchInterval: queryRefreshInterval(refreshMs),
		refetchIntervalInBackground: false,
	});
}

export function useApplicationsCountsQuery(refreshMs = 0) {
	return useQuery({
		queryKey: adminRequestKeys.counts,
		queryFn: getAdminRequestCounts,
		refetchInterval: queryRefreshInterval(refreshMs),
		refetchIntervalInBackground: false,
	});
}
